import type { DcpCheckStatus } from '@/lib/db-tenant'

/**
 * PostgREST disjunct(s) selecting the rows whose EFFECTIVE status is the key —
 * the server-side twin of `effectiveCheckStatus` in `sage-utils.ts`, which
 * resolves: explicit `dcp_check_status` override → `date_cashed` set (paid) →
 * issued. Change both together.
 *
 * The values are fixed tokens without PostgREST reserved characters, so no
 * quoting is needed; no caller input ever reaches these strings.
 *
 * Typed as a `Record` over the status union so adding a status fails to compile
 * here until its predicate is written.
 */
const EFFECTIVE_STATUS_DISJUNCTS: Record<DcpCheckStatus, string> = {
  issued: 'dcp_check_status.eq.issued,and(dcp_check_status.is.null,date_cashed.is.null)',
  paid: 'dcp_check_status.eq.paid,and(dcp_check_status.is.null,date_cashed.not.is.null)',
  voided: 'dcp_check_status.eq.voided',
}

/**
 * How a Status filter restricts `sage_checks`:
 *  - `all`   — no restriction (nothing selected, or every status selected);
 *  - `none`  — values were supplied but none is a known status → no rows;
 *  - `match` — apply `or` through `query.or(...)`. It is a single disjunction,
 *              so it ANDs with every other filter, including other `.or()`s.
 */
export type CheckStatusFilter =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'match'; or: string }

/**
 * Build the effective-status filter for `sageChecks.list` / `count`. Both call
 * this so the page rows and the pagination count can never disagree.
 *
 * Input is validated here because it can originate in UI state: unknown values
 * are dropped, and a list made only of unknown values matches nothing rather
 * than silently widening to every row. Duplicates and order do not matter; the
 * output follows the fixed issued → paid → voided order.
 *
 * @param statuses - Selected statuses; `undefined` or empty means "all".
 * @returns The filter to apply.
 */
export function checkStatusFilter(statuses: readonly string[] | undefined): CheckStatusFilter {
  if (!statuses || statuses.length === 0) return { kind: 'all' }

  const selected = new Set(statuses)
  const all = Object.entries(EFFECTIVE_STATUS_DISJUNCTS)
  const disjuncts = all.filter(([status]) => selected.has(status)).map(([, clause]) => clause)

  if (disjuncts.length === 0) return { kind: 'none' }
  // The three effective statuses partition every row (the DB CHECK constraint
  // limits the override to these values), so selecting all of them is no filter.
  if (disjuncts.length === all.length) return { kind: 'all' }
  return { kind: 'match', or: disjuncts.join(',') }
}
